import React from 'react';
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { BOOKING_THEME } from '../bookingTheme';
import type { BookerDetails } from '../hooks/useBookingFlow';
import { validateEmail, validatePhone } from '../hooks/useBookingFlow';
import {
  PHONE_COUNTRY_CODES,
  PHONE_COUNTRY_OPTIONS,
  findPhoneCountryOption,
  formatPhoneCountryOption,
} from '../countryCodes';

interface StepYourDetailsProps {
  details: BookerDetails;
  onChange: (partial: Partial<BookerDetails>) => void;
  showErrors?: boolean;
}

const fieldSx = {
  '& .MuiOutlinedInput-root fieldset': { borderColor: BOOKING_THEME.border },
  '& .MuiOutlinedInput-root': { borderRadius: 2 },
};

const StepYourDetails: React.FC<StepYourDetailsProps> = ({ details, onChange, showErrors }) => {
  const nameErr = showErrors && details.name.trim().length < 2 ? 'Name must be at least 2 characters' : null;
  const emailErr = showErrors ? validateEmail(details.email) : null;
  const phoneErr = showErrors ? validatePhone(details.phone, details.countryCode) : null;

  const countryCodeValue = PHONE_COUNTRY_CODES.includes(details.countryCode)
    ? details.countryCode
    : '+1';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: BOOKING_THEME.textPrimary }}>
        Step 2: Your Details
      </Typography>

      <Typography variant="body2" sx={{ color: BOOKING_THEME.textSecondary, mb: 0 }}>
        Your details will be shared with the consultant prior to the appointment.
      </Typography>

      <TextField
        label="Full name"
        required
        fullWidth
        size="small"
        value={details.name}
        onChange={(e) => onChange({ name: e.target.value })}
        error={!!nameErr}
        helperText={nameErr}
        sx={fieldSx}
      />

      <TextField
        label="Email address"
        required
        fullWidth
        size="small"
        type="email"
        autoComplete="email"
        value={details.email}
        onChange={(e) => onChange({ email: e.target.value })}
        error={!!emailErr}
        helperText={emailErr}
        sx={fieldSx}
      />

      <Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            select
            label="Country"
            size="small"
            value={countryCodeValue}
            onChange={(e) => onChange({ countryCode: e.target.value })}
            SelectProps={{
              renderValue: (value) => {
                const option = findPhoneCountryOption(String(value));
                return option ? formatPhoneCountryOption(option) : String(value);
              },
              MenuProps: {
                PaperProps: { sx: { maxHeight: 360 } },
              },
            }}
            sx={{
              minWidth: { xs: 140, sm: 220 },
              maxWidth: { xs: '42%', sm: 'none' },
              flex: { sm: '0 0 auto' },
              ...fieldSx,
            }}
          >
            {PHONE_COUNTRY_OPTIONS.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {formatPhoneCountryOption(option)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Phone number"
            required
            fullWidth
            size="small"
            type="tel"
            autoComplete="tel-national"
            value={details.phone}
            onChange={(e) => onChange({ phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
            error={!!phoneErr}
            sx={fieldSx}
          />
        </Box>
        {phoneErr && (
          <FormHelperText error sx={{ ml: 1.5 }}>{phoneErr}</FormHelperText>
        )}
      </Box>

      <TextField
        label="Notes (optional)"
        fullWidth
        size="small"
        multiline
        rows={3}
        value={details.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="Describe your concern or question..."
        sx={fieldSx}
      />
    </Box>
  );
};

export default StepYourDetails;
